/**
 * Single source of truth for subscription tiers (frontend).
 *
 * Mirrors `/app/backend/plan_limits.py` — keep both in sync.
 *
 * Each tier defines:
 *   key             - DB identifier; matches backend
 *   name, tagline   - marketing copy
 *   icon            - emoji shown on the pricing card
 *   prices          - monthly + annual prices in GBP (already include 17% off annual)
 *   limits          - hard caps used by the enforcement layer
 *   features        - bulleted feature list for the pricing card
 *   recommended     - which tier to highlight visually
 *   trialDays       - free trial length (0 = no trial)
 *   ctaLabel        - call-to-action label on the card
 *   stripe          - LEGACY placeholders for Stripe Price IDs (migrated to Mollie;
 *                     these fields are now unused but kept so existing JSON consumers
 *                     don't trip on a missing key)
 *
 * Pricing rationale (UK childcare SaaS market scan, 2026):
 *   £29   ~ childminder / micro-nursery (1 site, ≤25 kids)
 *   £69   ~ standard single-site nursery
 *   £129  ~ established setting / 2 sites
 *   £249+ ~ multi-site chains, custom contracts
 */
export const ANNUAL_DISCOUNT_PCT = 17;

export const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    icon: '🌱',
    tagline: 'For childminders and micro-nurseries getting started.',
    trialDays: 14,
    prices: { monthly: 29, annual: 290 },     // £290/yr = ~17% off £348
    limits: {
      max_children: 25,
      max_staff: 5,
      max_centers: 1,
      recap_videos_per_month: 1,
    },
    features: [
      'Up to 25 children · 5 staff · 1 centre',
      'Bookings, daily roster, register',
      'Parent app + messaging',
      'Moments & photo feeds',
      'Email support',
      '1 recap video / month',
    ],
    excludedFeatures: [
      'Events module',
      'Payroll PDFs',
      'Custom branding',
    ],
    ctaLabel: 'Start free trial',
    recommended: false,
  },
  {
    key: 'growth',
    name: 'Growth',
    icon: '🌳',
    tagline: 'Single-site nurseries who need the full toolkit.',
    trialDays: 14,
    prices: { monthly: 69, annual: 690 },     // £690/yr = ~17% off £828
    limits: {
      max_children: 80,
      max_staff: 15,
      max_centers: 1,
      recap_videos_per_month: 3,
    },
    features: [
      'Up to 80 children · 15 staff · 1 centre',
      'Everything in Starter, plus:',
      'Events module + day-of register',
      'Timesheets + payroll PDFs',
      'Per-org branding',
      'Email + chat support',
      '3 recap videos / month',
    ],
    excludedFeatures: [
      'Custom subdomain',
      'Staff compliance catalogue',
    ],
    ctaLabel: 'Start free trial',
    recommended: true,
  },
  {
    key: 'professional',
    name: 'Professional',
    icon: '🌲',
    tagline: 'Established settings and growing 2-site groups.',
    trialDays: 14,
    prices: { monthly: 129, annual: 1290 },   // £1290/yr = ~17% off £1548
    limits: {
      max_children: 200,
      max_staff: 40,
      max_centers: 2,
      recap_videos_per_month: 999, // effectively unlimited
    },
    features: [
      'Up to 200 children · 40 staff · 2 centres',
      'Everything in Growth, plus:',
      'Custom subdomain',
      'Staff compliance catalogue',
      'Hard-block scheduling for non-compliant staff',
      'Unlimited recap videos',
      'Phone support',
    ],
    excludedFeatures: [
      'Dedicated CSM',
      'SLA',
    ],
    ctaLabel: 'Start free trial',
    recommended: false,
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    icon: '🏢',
    tagline: 'Multi-site groups, councils, and franchises.',
    trialDays: 0,
    prices: { monthly: 249, annual: 2490, fromPrice: true },
    limits: {
      max_children: null,       // unlimited
      max_staff: null,
      max_centers: null,
      recap_videos_per_month: null,
    },
    features: [
      'Unlimited children, staff, centres',
      'Everything in Professional, plus:',
      'Dedicated CSM',
      'Custom SLA',
      'SSO / SAML on request',
      'Bespoke onboarding + data migration',
      'SMS safeguarding pack included',
    ],
    excludedFeatures: [],
    ctaLabel: 'Contact sales',
    recommended: false,
    isCustom: true,
  },
];

/** Convert a plan key + interval into a display price string in GBP. */
export function formatPrice(plan, interval = 'monthly') {
  if (!plan) return '£0';
  const p = plan.prices[interval];
  if (p == null) return '—';
  const prefix = plan.prices.fromPrice ? 'from ' : '';
  return `${prefix}£${p.toLocaleString('en-GB')}`;
}

/** Marketing copy for the annual savings badge. */
export function annualSavings(plan) {
  if (!plan || !plan.prices.monthly || !plan.prices.annual) return null;
  const fullYear = plan.prices.monthly * 12;
  const saved = fullYear - plan.prices.annual;
  if (saved <= 0) return null;
  return `Save £${saved.toLocaleString('en-GB')}/yr`;
}
